import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Typography,
  Box,
  Button,
  Chip,
  Paper,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Rating,
  LinearProgress,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  PictureAsPdf as PdfIcon,
  Email as EmailIcon,
  ThumbUp as ThumbUpIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { progressService } from '../../services/progress.service';
import { SummaryData } from '../../types/dashboard.types';
import { useNotification } from '../../context/NotificationContext';
import { feedbackService } from '../../services/feedback.service';
import { FeedbackAggregate } from '../../types/feedback.types';

const SessionSummary: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  showNotificationRef.current = showNotification;

  const [data, setData] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [feedbackAggregate, setFeedbackAggregate] = useState<FeedbackAggregate | null>(null);

  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      const result = await progressService.getSessionSummary(id);
      setData(result);
    } catch {
      showNotificationRef.current('Failed to load session summary', 'error');
    } finally {
      setLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadData();
    if (id) {
      feedbackService.getAggregate(id).then(setFeedbackAggregate).catch(() => {
        // Silently fail — feedback section just won't show
      });
    }
  }, [loadData, id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!data) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="text.secondary">Failed to load session summary.</Typography>
        <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2 }}>Go Back</Button>
      </Box>
    );
  }

  const getRankDisplay = (rank: number) => {
    if (rank === 1) return <Tooltip title="1st Place"><Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', bgcolor: '#ffd700', color: '#8b6914', fontWeight: 600, fontSize: 13 }}>1</Box></Tooltip>;
    if (rank === 2) return <Tooltip title="2nd Place"><Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', bgcolor: '#c0c0c0', color: '#666', fontWeight: 600, fontSize: 13 }}>2</Box></Tooltip>;
    if (rank === 3) return <Tooltip title="3rd Place"><Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', bgcolor: '#cd7f32', color: '#fff', fontWeight: 600, fontSize: 13 }}>3</Box></Tooltip>;
    return <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', bgcolor: 'grey.200', color: 'text.secondary', fontWeight: 600, fontSize: 13 }}>{rank}</Box>;
  };

  const getBarColor = (percent: number) => {
    if (percent >= 80) return 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)';
    if (percent >= 50) return 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)';
    return 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)';
  };

  const sessionDuration = data.session.actualStart && data.session.endedAt
    ? Math.round((new Date(data.session.endedAt).getTime() - new Date(data.session.actualStart).getTime()) / 60000)
    : data.session.duration;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            size="small"
            startIcon={<BackIcon />}
            onClick={() => navigate(`/instructor/session/${id}`)}
            sx={{ color: 'text.secondary' }}
          >
            Back
          </Button>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Session Summary & Analytics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PdfIcon />}
            onClick={() => showNotification('PDF export coming soon', 'info')}
          >
            Export PDF
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EmailIcon />}
            onClick={() => showNotification('Email report coming soon', 'info')}
          >
            Email Report
          </Button>
        </Box>
      </Box>

      {/* Session Header Card */}
      <Paper
        elevation={2}
        sx={{
          background: (theme: any) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white',
          borderRadius: 3,
          p: 4,
          mb: 4,
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          {data.session.workshopTitle}
        </Typography>
        <Typography variant="subtitle1" sx={{ opacity: 0.9, mb: 1 }}>
          {data.session.name}
        </Typography>
        {data.session.status === 'completed' && (
          <Chip
            label="Session Completed"
            sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 600, mb: 2 }}
          />
        )}
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {data.session.actualStart ? dayjs(data.session.actualStart).format('MMM D, YYYY') : '--'}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: 13 }}>Session Date</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {sessionDuration} min
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: 13 }}>Duration</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {data.stats.totalStudents}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: 13 }}>Total Participants</Typography>
            </Box>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {data.session.status.charAt(0).toUpperCase() + data.session.status.slice(1)}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9, fontSize: 13 }}>Status</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
              Completion Rate
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: data.stats.completionRate >= 85 ? 'success.main' : 'primary.main',
                mb: 0.5,
              }}
            >
              {data.stats.completionRate}%
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {data.stats.completedStudents} of {data.stats.totalStudents} students completed
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
              Help Requests
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: data.stats.totalHelpRequests > 0 ? '#f57c00' : 'text.primary',
                mb: 0.5,
              }}
            >
              {data.stats.totalHelpRequests}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total help requests during session
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
              Avg Response Time
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
              {data.stats.avgResponseMinutes}
              <Typography component="span" variant="h6" sx={{ fontWeight: 500, ml: 0.5 }}>
                min
              </Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Average time to resolve requests
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
              Feedback Rate
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
              {feedbackAggregate && data.stats.totalStudents > 0
                ? `${Math.round((feedbackAggregate.count / data.stats.totalStudents) * 100)}%`
                : '--'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {feedbackAggregate
                ? `${feedbackAggregate.count} of ${data.stats.totalStudents} students responded`
                : 'No feedback data'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Step Completion Bars */}
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          Completion by Step
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {data.stepCompletion.map((step) => (
            <Box key={step.stepId} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 500, width: 200, flexShrink: 0, color: 'text.secondary' }}
              >
                Step {step.orderIndex + 1}: {step.stepTitle}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  bgcolor: 'grey.100',
                  borderRadius: 1.5,
                  height: 32,
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${step.completionPercent}%`,
                    background: getBarColor(step.completionPercent),
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    px: 1.5,
                    transition: 'width 1s ease-out',
                    minWidth: step.completionPercent > 0 ? 50 : 0,
                  }}
                >
                  {step.completionPercent > 15 && (
                    <Typography variant="caption" sx={{ color: 'white', fontWeight: 600, fontSize: 13 }}>
                      {step.completedCount}/{step.totalStudents}
                    </Typography>
                  )}
                </Box>
              </Box>
              {step.completionPercent <= 15 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, minWidth: 40 }}>
                  {step.completedCount}/{step.totalStudents}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </Paper>

      {/* Student Feedback */}
      {feedbackAggregate && (
        <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Student Feedback
            </Typography>
            {feedbackAggregate.count > 0 && (
              <Typography variant="body2" color="text.secondary">
                {feedbackAggregate.count} {feedbackAggregate.count === 1 ? 'response' : 'responses'}
              </Typography>
            )}
          </Box>

          {feedbackAggregate.count === 0 && (
            <Typography variant="body2" color="text.secondary">
              No feedback submitted yet.
            </Typography>
          )}

          {feedbackAggregate.count === 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'info.main' }}>
              <InfoIcon fontSize="small" />
              <Typography variant="body2">
                Feedback data is shown only when 2 or more students have submitted to protect anonymity.
              </Typography>
            </Box>
          )}

          {feedbackAggregate.count >= 2 && (
            <>
              {/* Rating Cards */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {[
                  { label: 'Overall', value: feedbackAggregate.averageOverall },
                  { label: 'Content Clarity', value: feedbackAggregate.averageContentClarity },
                  { label: 'Hands-on Relevance', value: feedbackAggregate.averageHandsOnRelevance },
                  { label: 'Environment Quality', value: feedbackAggregate.averageEnvironmentQuality },
                ].map((rating) => (
                  <Grid size={{ xs: 6, sm: 3 }} key={rating.label}>
                    <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'grey.50' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        {rating.label}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, my: 0.5 }}>
                        {rating.value !== null ? `${rating.value}/5` : '--'}
                      </Typography>
                      <Rating
                        value={rating.value ?? 0}
                        precision={0.1}
                        readOnly
                        size="small"
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>

              {/* Difficulty Distribution */}
              {feedbackAggregate.difficultyDistribution && (() => {
                const dist = feedbackAggregate.difficultyDistribution!;
                const totalResponses = Object.values(dist).reduce((a, b) => a + b, 0);
                return (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                      Difficulty Distribution
                    </Typography>
                    {[
                      { key: 'too_easy', label: 'Too Easy' },
                      { key: 'easy', label: 'Easy' },
                      { key: 'just_right', label: 'Just Right' },
                      { key: 'hard', label: 'Hard' },
                      { key: 'too_hard', label: 'Too Hard' },
                    ].map((item) => {
                      const count = dist[item.key as keyof typeof dist] ?? 0;
                      const percent = totalResponses > 0 ? Math.round((count / totalResponses) * 100) : 0;
                      return (
                        <Box key={item.key} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                          <Typography variant="body2" sx={{ width: 80, flexShrink: 0, fontSize: 13 }}>
                            {item.label}
                          </Typography>
                          <Box sx={{ flex: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={percent}
                              sx={{ height: 8, borderRadius: 4 }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ width: 36, textAlign: 'right', fontSize: 13, fontWeight: 500 }}>
                            {percent}%
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}

              {/* Recommendation Rate */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ThumbUpIcon fontSize="small" color="success" />
                {feedbackAggregate.recommendationRate !== null ? (
                  <Typography variant="body2">
                    <strong>{feedbackAggregate.recommendationRate}%</strong> would recommend
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No recommendation data yet
                  </Typography>
                )}
              </Box>

              {/* Anonymous Comments */}
              {feedbackAggregate.recentComments && feedbackAggregate.recentComments.length > 0 && (() => {
                const workedWell = feedbackAggregate.recentComments!
                  .map((c) => c.whatWorkedWell)
                  .filter((t): t is string => !!t)
                  .slice(0, 5);
                const couldImprove = feedbackAggregate.recentComments!
                  .map((c) => c.whatCouldImprove)
                  .filter((t): t is string => !!t)
                  .slice(0, 5);

                if (workedWell.length === 0 && couldImprove.length === 0) return null;

                return (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                      Student Comments
                    </Typography>
                    {workedWell.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                          What worked well:
                        </Typography>
                        {workedWell.map((comment, i) => (
                          <Typography key={i} variant="body2" sx={{ pl: 2, mb: 0.5 }}>
                            &bull; &ldquo;{comment}&rdquo;
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {couldImprove.length > 0 && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5 }}>
                          What could be improved:
                        </Typography>
                        {couldImprove.map((comment, i) => (
                          <Typography key={i} variant="body2" sx={{ pl: 2, mb: 0.5 }}>
                            &bull; &ldquo;{comment}&rdquo;
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </>
                );
              })()}
            </>
          )}
        </Paper>
      )}

      {/* Student Performance Table */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Student Performance
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Rank</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Student</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Completion</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Help Requests</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.students.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">No student data available.</Typography>
                  </TableCell>
                </TableRow>
              )}
              {data.students.map((student, idx) => (
                <TableRow key={student.studentId} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                  <TableCell>{getRankDisplay(idx + 1)}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {student.studentName || 'Unknown'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`${student.completionPercent}% (${student.completedSteps}/${student.totalSteps})`}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        fontSize: 12,
                        bgcolor: student.completionPercent === 100 ? '#e8f5e9' : '#e3f2fd',
                        color: student.completionPercent === 100 ? 'success.main' : 'primary.main',
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {student.timeMinutes !== null ? `${student.timeMinutes} min` : '--'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {student.helpRequests}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default SessionSummary;
